import PropTypes from 'prop-types';

import useMediaQuery from '@mui/material/useMediaQuery';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';

import { useGetMenuMaster } from 'api/menu';
import { DRAWER_WIDTH, MINI_DRAWER_WIDTH } from 'config';

import CloseOutlined from '@ant-design/icons/CloseOutlined';

const MUI_MAX_WIDTH_MAP = {
  xs: 444,
  sm: 600,
  md: 900,
  lg: 1200,
  xl: 1536
};

export default function CommonFormModal({
  open,
  onClose,
  onSubmit,
  title,
  children,
  submitLabel = 'Save',
  cancelLabel = 'Cancel',
  maxWidth = 'md',
  disableSubmit = false,
  showCloseIcon = false,
  hideCancelButton = false,
  titleSx,
  titleVariant = 'h6',
  closeIconSx
}) {
  const downLG = useMediaQuery((theme) => theme.breakpoints.down('lg'));
  const { menuMaster } = useGetMenuMaster();
  const drawerOpen = menuMaster.isDashboardDrawerOpened;
  const drawerSideWidth = downLG ? 0 : drawerOpen ? DRAWER_WIDTH : MINI_DRAWER_WIDTH;
  const modalCap = MUI_MAX_WIDTH_MAP[maxWidth] || MUI_MAX_WIDTH_MAP.md;
  const horizontalGutter = 32;
  const contentMaxWidth = `min(${modalCap}px, calc(100vw - ${drawerSideWidth}px - ${horizontalGutter * 2}px))`;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth={false}
      scroll="paper"
      sx={{
        '& .MuiDialog-container': {
          alignItems: 'center',
          justifyContent: 'center',
          px: { xs: 2, lg: 0 }
        },
        '& .MuiDialog-paper': {
          width: '100%',
          maxWidth: {
            xs: `calc(100vw - ${horizontalGutter}px)`,
            lg: contentMaxWidth
          },
          ml: { xs: 'auto', lg: `${drawerSideWidth}px` },
          mr: { xs: 'auto', lg: `${horizontalGutter / 2}px` },
          my: { xs: 2, sm: 3 },
          maxHeight: { xs: 'calc(100% - 32px)', sm: 'calc(100% - 48px)' }
        }
      }}
    >
      <DialogTitle sx={titleSx}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
          <Typography variant={titleVariant}>{title}</Typography>
          {showCloseIcon && (
            <IconButton onClick={onClose} size="small" aria-label="close modal" sx={closeIconSx}>
              <CloseOutlined />
            </IconButton>
          )}
        </Box>
      </DialogTitle>
      <DialogContent dividers sx={{ px: { xs: 2, sm: 3 } }}>
        {children}
      </DialogContent>
      <DialogActions
        sx={{
          gap: 2,
          pr: { xs: 2, sm: 3 },
          pl: { xs: 2, sm: 3 },
          py: { xs: 2, sm: 3 },
          flexWrap: 'wrap'
        }}
      >
        {!hideCancelButton && (
          <Button
            sx={{
              px: 4,
              py: 1.5,
              fontSize: '1rem'
            }}
            onClick={onClose}
          >
            {cancelLabel}
          </Button>
        )}
        <Button
          sx={{
            px: 4,
            py: 1,
            fontSize: '1rem'
          }}
          variant="contained"
          onClick={onSubmit}
          disabled={disableSubmit}
        >
          {submitLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

CommonFormModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
  submitLabel: PropTypes.string,
  cancelLabel: PropTypes.string,
  maxWidth: PropTypes.oneOf(['xs', 'sm', 'md', 'lg', 'xl']),
  disableSubmit: PropTypes.bool,
  showCloseIcon: PropTypes.bool,
  hideCancelButton: PropTypes.bool,
  titleSx: PropTypes.object,
  titleVariant: PropTypes.string,
  closeIconSx: PropTypes.object
};
